<template>
  <div class="bg-gray-100 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-7xl mx-auto text-center">
      <h1 class="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-6">
        Unlock Exclusive Content & Stay Updated
      </h1>
      <p class="text-lg text-gray-600 mb-12">
        Choose a subscription plan that suits you best and enjoy uninterrupted access.
      </p>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">

        <!-- Newspaper -->
        <div class="bg-white shadow-lg rounded-xl p-6 min-h-[350px] flex flex-col justify-between hover:shadow-xl transition">
          <div>
            <h2 class="text-2xl font-semibold text-[#D84040] mb-2">Newspaper</h2>
            <p class="text-gray-700 text-xl font-bold mb-4">₹20</p>
            <p class="text-gray-600 mb-6">Get access to daily news and updates tailored just for you.</p>
          </div>
          <button @click="openModal('Newspaper')" class="bg-[#D84040] text-white px-4 py-2 rounded-md hover:bg-[#b73232] w-full">
            Subscribe
          </button>
        </div>

        <!-- Magazine -->
        <div class="bg-white shadow-lg rounded-xl p-6 min-h-[350px] flex flex-col justify-between hover:shadow-xl transition">
          <div>
            <h2 class="text-2xl font-semibold text-[#D84040] mb-2">Magazine</h2>
            <p class="text-gray-700 text-xl font-bold mb-4">₹100</p>
            <p class="text-gray-600 mb-6">Receive a curated newsletter every week with top stories.</p>
          </div>
          <button @click="openModal('Magazine')" class="bg-[#D84040] text-white px-4 py-2 rounded-md hover:bg-[#b73232] w-full">
            Subscribe
          </button>
        </div>

      </div>
    </div>
  </div>

  <!-- Terms Modal -->
  <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
    <div class="bg-white w-full max-w-3xl max-h-[80vh] overflow-y-auto rounded-xl shadow-lg p-6">

      <h2 class="text-2xl font-bold text-[#D84040] mb-4 text-center">Terms & Policies</h2>

      <div class="text-sm text-gray-700 space-y-4 leading-relaxed">

        <p><strong>TERMS & CONDITIONS</strong></p>
        <p><strong>Effective Date:</strong> 1-1-2026</p>
        <p>Welcome to Cultural Megazine Dapon. By using our services you agree to the following:</p>

        <p><strong>1. Scope of Services</strong><br>
        Subscription services, cultural events, workshops, ticketing and registrations.</p>

        <p><strong>2. Eligibility</strong><br>
        Users must be 18+ or under guardian supervision.</p>

        <p><strong>3. Payments</strong><br>
        Processed via secure third-party gateways. We do not store card/UPI/bank data.</p>

        <p><strong>4. Subscriptions</strong><br>
        Time-bound plans begin after successful payment. Renewal is user responsibility.</p>

        <p><strong>5. Events</strong><br>
        Organizer may change schedules or venues due to unavoidable reasons.</p>

        <p><strong>6. Intellectual Property</strong><br>
        All content belongs to Cultural Megazine Dapon.</p>

        <p><strong>7. Limitation of Liability</strong><br>
        Not liable for gateway failures, force majeure, or third-party services.</p>

        <p><strong>8. Governing Law</strong><br>
        Laws of India, jurisdiction Kamrup(M) courts.</p>

        <hr>

        <p><strong>PRIVACY POLICY</strong></p>
        <p>We collect basic details like name, phone, email, and address for subscriptions/events. No financial data is stored. Data is shared only with gateways, delivery partners, or legal authorities when required.</p>

        <hr>

        <p><strong>REFUND POLICY</strong></p>
        <p>Digital subscriptions are non-refundable after activation. Event refunds only for organizer cancellation or reschedule. 7–10 working days processing time.</p>

        <hr>

        <p><strong>CANCELLATION POLICY</strong></p>
        <p>Users may cancel renewal anytime. No refund for active subscription period.</p>

        <hr>

        <p><strong>CONTACT</strong><br>
        Cultural Megazine Dapon<br>
        House No 34, Forest Gate, Hengrabari, Guwahati-36, Assam<br>
        sanskritirdapoon@gmail.com<br>
        9864037776</p>

      </div>

      <!-- Consent Checkbox -->
      <label class="flex items-start gap-2 mt-6 text-xs text-gray-600">
        <input type="checkbox" v-model="termsAccepted" class="mt-1" />
        I agree to the Terms & Conditions, Privacy Policy, Refund and Cancellation Policy.
      </label>

      <!-- Buttons -->
      <div class="flex justify-end gap-4 mt-6">
        <button @click="showModal=false" class="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400">
          Reject
        </button>
        <button
          @click="acceptTerms"
          :disabled="!termsAccepted"
          :class="termsAccepted ? 'bg-[#D84040] hover:bg-[#b73232]' : 'bg-gray-400 cursor-not-allowed'"
          class="px-4 py-2 text-white rounded-md">
          Accept & Continue
        </button>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import Swal from 'sweetalert2'

const route = useRoute()
const router = useRouter()
const showModal = ref(false)
const selectedPlan = ref(null)
const termsAccepted = ref(false)

const purchaseType = ref(null)
const purchaseId = ref(null)

onMounted(() => {
  if (route.query.type && route.query.id) {
    purchaseType.value = route.query.type
    purchaseId.value = route.query.id
    
    // Automatically select plan based on type
    const plan = route.query.type === 'magazine' ? 'Magazine' : 'Newspaper'
    openModal(plan)
  }
})

// ✅ Vue CLI uses process.env.VUE_APP_*
const API_BASE = process.env.VUE_APP_API_BASE_URL || 'https://sanskritirdapoon.in/api'

const openModal = (plan) => {
  selectedPlan.value = plan
  termsAccepted.value = false
  showModal.value = true
}

const acceptTerms = async () => {
  showModal.value = false

  const amount = '1.00' // selectedPlan.value === 'Magazine' ? '100.00' : '20.00'
  const plan = selectedPlan.value

  let user = null;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      user = JSON.parse(userStr);
    }
  } catch (e) {
    console.error('Failed to parse user from localStorage', e);
  }

  const payload = { 
    amount, 
    plan,
    userId: user?.id,
    userEmail: user?.email,
    userContact: user?.contact
  };

  try {
    console.log(`💳 Initiating Paytm payment — plan: ${plan} | amount: ₹${amount}`);

    const res = await axios.post(`${API_BASE}/paytm/initiate`, payload)

    console.log('✅ Backend Response:', res.data)

    // New backend returns txnToken directly at top level
    if (res.data.success && res.data.txnToken) {
      console.log('🎯 Token received:', res.data.txnToken)
      
      // 👉 NEW LOGIC: Link purchase in DB before opening checkout
      if (purchaseType.value && purchaseId.value) {
        const endpoint = purchaseType.value === 'magazine' ? 'magazines' : 'newspapers'
        try {
          const userToken = localStorage.getItem('token')
          await axios.post(`${API_BASE}/${endpoint}/${purchaseId.value}/purchase`, {
            order_id: res.data.orderId,
            amount: res.data.amount
          }, {
            headers: { Authorization: `Bearer ${userToken}` }
          })
          console.log(`✅ Pending purchase created for ${purchaseType.value} ${purchaseId.value}`)
        } catch (dbErr) {
          console.error('❌ Failed to create pending purchase in DB', dbErr)
          Swal.fire({ icon: 'error', title: 'Database Error', text: 'Could not prepare your purchase. Please try again.' })
          return // Abort checkout if DB link fails
        }
      }

      loadPaytmAndCheckout(res.data.txnToken, res.data.orderId, res.data.amount, res.data.mid)
    } else {
      // Show the Paytm resultCode so developer/team can see it
      const code = res.data.resultCode || '—'
      const msg  = res.data.error || 'Unable to start transaction'
      console.error('❌ Paytm Error — resultCode:', code, '| msg:', msg)

      Swal.fire({
        icon: 'error',
        title: 'Payment Gateway Error',
        html: `<b>${msg}</b><br><small style="color:#999">resultCode: ${code}</small>`,
        confirmButtonColor: '#BF3131'
      })
    }

  } catch (err) {
    console.error('❌ Payment initiation error:', err.response?.data || err.message)
    const errMsg = err.response?.data?.error || err.message
    const errCode = err.response?.data?.resultCode || '—'

    Swal.fire({
      icon: 'error',
      title: 'Initiation Failed',
      html: `<b>${errMsg}</b><br><small style="color:#999">resultCode: ${errCode}</small>`,
      confirmButtonColor: '#BF3131'
    })
  }
}

// Dynamically load the Paytm JS SDK (production) then invoke checkout
const loadPaytmAndCheckout = (txnToken, orderId, amount, mid) => {
  // Remove old script if re-paying
  const old = document.getElementById('paytm-checkout-sdk')
  if (old) old.remove()

  const script = document.createElement('script')
  script.id = 'paytm-checkout-sdk'
  script.src = `https://secure.paytmpayments.com/merchantpgpui/checkoutjs/merchants/${mid}.js`
  script.crossOrigin = 'anonymous'

  script.onload = () => {
    if (window.Paytm?.CheckoutJS) {
      window.Paytm.CheckoutJS.onLoad(() => {
        window.Paytm.CheckoutJS.init({
          root: '',
          flow: 'DEFAULT',
          merchant: {
            mid: mid,
            redirect: false
          },
          data: {
            orderId,
            token: txnToken,
            tokenType: 'TXN_TOKEN',
            amount,
          },
          handler: {
            notifyMerchant: (eventName, data) => {
              console.log('Paytm event:', eventName, data)
            },
            transactionStatus: async (data) => {
              console.log('Transaction status:', data)
              if (data.STATUS === 'TXN_SUCCESS') {
                // 👉 NEW LOGIC: Confirm purchase in DB
                if (purchaseType.value && purchaseId.value) {
                  const endpoint = purchaseType.value === 'magazine' ? 'magazines' : 'newspapers'
                  try {
                    const userToken = localStorage.getItem('token')
                    await axios.post(`${API_BASE}/${endpoint}/${purchaseId.value}/purchase/confirm`, {
                      order_id: data.ORDERID,
                      txn_id: data.TXNID
                    }, {
                      headers: { Authorization: `Bearer ${userToken}` }
                    })
                    console.log('✅ Purchase confirmed in DB')
                  } catch (confirmErr) {
                    console.error('Failed to confirm purchase in DB', confirmErr)
                  }
                }

                Swal.fire({ icon: 'success', title: '🎉 Payment Successful!', text: 'Transaction ID: ' + data.TXNID, confirmButtonColor: '#2e7d32' }).then(() => {
                  // Redirect to homepage to download
                  router.push('/')
                })
              } else {
                Swal.fire({ icon: 'warning', title: 'Payment ' + (data.STATUS || 'Incomplete'), text: data.RESPMSG || 'Please try again.', confirmButtonColor: '#BF3131' })
              }
            }
          }
        }).then(() => window.Paytm.CheckoutJS.invoke())
          .catch(e => { console.error('Paytm init error:', e); Swal.fire({ icon: 'error', title: 'Checkout Error', text: String(e) }) })
      })
    }
  }

  script.onerror = () => {
    console.error('Failed to load Paytm SDK')
    Swal.fire({ icon: 'error', title: 'SDK Load Failed', text: 'Could not load Paytm checkout. Please try again.', confirmButtonColor: '#BF3131' })
  }

  document.body.appendChild(script)
}
</script>
